<template>
  <div class="survey-form">
    <AppSelectField
      v-model="selectedTemplateId"
      :items="templateOptions"
      item-title="title"
      item-value="value"
      label="Choose Survey template"
      placeholder="Select a template"
    />

    <div v-if="selectedTemplate" class="questions">
      <div class="questions__title my-4">{{ selectedTemplate.name }}</div>
      <div class="questions__list">
        <div v-for="q in selectedQuestions" :key="q.key" class="questions__item mb-4">
          <div class="question__text">{{ q.text }}</div>

          <AppTextField
            v-if="q.type === 'text'"
            v-model="answers[q.key]"
            placeholder="Type your answer"
          />

          <v-textarea
            v-else-if="q.type === 'textarea'"
            v-model="answers[q.key]"
            auto-grow
            placeholder="Type your answer"
            variant="solo"
            flat
            hide-details="auto"
            class="question__textarea"
          />

          <v-radio-group
            v-else-if="q.type === 'radio'"
            v-model="answers[q.key]"
            hide-details="auto"
            class="question__radio"
          >
            <v-radio
              v-for="opt in q.options"
              :key="opt.value"
              :label="opt.label"
              :value="opt.value"
            />
          </v-radio-group>

          <div v-else-if="q.type === 'checkbox'" class="question__checkbox">
            <v-checkbox
              v-for="opt in q.options"
              :key="opt.value"
              v-model="answers[q.key]"
              :label="opt.label"
              :value="opt.value"
              hide-details="auto"
            />
          </div>

          <v-date-input
            v-else-if="q.type === 'date'"
            v-model="answers[q.key]"
            variant="solo"
            flat
            hide-details="auto"
            prepend-icon=""
            placeholder="Select a date"
            class="question__date"
          />

          <div v-else class="question__unsupported">
            Unsupported question type: {{ q.type }}
          </div>
        </div>
      </div>
      <!-- <div class="mt-3">
          <AppButtonFlat block @click="handleSubmitSurvey()">Submit</AppButtonFlat>
      </div> -->
    </div>
    <div v-else class="empty-state">
      <div class="empty-state__icon">
        <v-icon size="36" color="primary">mdi-clipboard-text-outline</v-icon>
      </div>
      <div class="empty-state__title">Select a survey template</div>
      <div class="empty-state__subtitle">
        Choose a template from the dropdown above to load questions and start filling out
        the survey for this appointment.
      </div>
    </div>
  </div>
  <div class="survey-action-section" v-if="selectedTemplate">
    <AppButtonFlat block @click="handleSubmitSurvey()" 
    :loading="isSubmitting"
    append-icon="mdi-send"
      >Submit</AppButtonFlat
    >
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, reactive, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import AppSelectField from "@/shared/components/ui/AppSelectField.vue";
import AppTextField from "@/shared/components/ui/AppTextField.vue";
import { fetchSurveySavedTemplates } from "@/shared/services/surveys.api";
import AppButtonFlat from "@/shared/components/ui/AppButtonFlat.vue";
import { submitAppointmentSurvey } from "@/shared/services/appointments.api";

type SurveyQuestion = {
  id?: number;
  question: string;
  question_type: string;
  question_code: string;
  options: Array<string | number | { label: string; value: string | number }>;
  answer?: unknown;
};
type SurveyTemplate = Record<string, unknown>;

type QuestionType = "text" | "textarea" | "radio" | "checkbox" | "date" | string;
type QuestionOption = { label: string; value: string | number };

const surveyTemplates = ref<SurveyTemplate[]>([]);
const selectedTemplateId = ref<string | number | "">("");
const answers = reactive<Record<string, unknown>>({});
const route = useRoute();
const router = useRouter();
const isSubmitting = ref(false);
function getTemplateTitle(template: SurveyTemplate): string {
  const title =
    template.title ?? template.name ?? template.template_name ?? template.label;
  return title ? String(title) : "Untitled template";
}

function getTemplateId(template: SurveyTemplate): string | number {
  const id = template.id ?? template.template_id ?? template.uuid ?? template.slug;
  return typeof id === "number" || typeof id === "string"
    ? id
    : getTemplateTitle(template);
}

function extractQuestions(template: SurveyTemplate): SurveyQuestion[] {
  const list =
    (template as any)?.template_data ?? (template as any)?.questions ?? template;
  return Array.isArray(list) ? (list as SurveyQuestion[]) : [];
}

function coerceDateAnswer(value: unknown): unknown {
  if (value == null || value === "") return null;
  if (value instanceof Date) return value;
  if (typeof value === "number") {
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  if (typeof value === "string") {
    // Accept ISO strings or yyyy-mm-dd; let Date parse and fall back to raw.
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? value : d;
  }
  return value;
}

function coerceCheckboxAnswer(value: unknown): unknown[] {
  if (Array.isArray(value)) return value;
  // Some backends store multi-select as comma-separated string.
  if (typeof value === "string") {
    const parts = value
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);
    return parts.length ? parts : [];
  }
  return [];
}

function normalizeQuestionType(raw: unknown): QuestionType {
  const type = String(raw ?? "text")
    .toLowerCase()
    .trim();
  if (type === "text") return "text";
  if (type === "textarea") return "textarea";
  if (type === "radio") return "radio";
  if (type === "checkbox") return "checkbox";
  if (type === "date") return "date";
  return type;
}

function extractOptions(question: SurveyQuestion): QuestionOption[] {
  const raw = question.options;
  if (!Array.isArray(raw)) return [];
  return raw
    .map((opt, idx) => {
      if (typeof opt === "string") return { label: opt, value: opt };
      if (typeof opt === "number") return { label: String(opt), value: opt };
      if (opt && typeof opt === "object") {
        const label =
          (opt as any).label ?? String((opt as any).value ?? `Option ${idx + 1}`);
        const value = (opt as any).value ?? label;
        if (typeof value !== "string" && typeof value !== "number") return null;
        return { label: String(label), value };
      }
      return null;
    })
    .filter((v): v is QuestionOption => Boolean(v));
}

const templateOptions = computed(() =>
  surveyTemplates.value.map((t) => ({
    title: getTemplateTitle(t),
    value: getTemplateId(t),
  }))
);

const selectedTemplate = computed(() => {
  if (selectedTemplateId.value === "") return null;
  return (
    surveyTemplates.value.find((t) => getTemplateId(t) === selectedTemplateId.value) ??
    null
  );
});

const selectedQuestions = computed(() => {
  const t = selectedTemplate.value;
  if (!t) return [];
  const questions = extractQuestions(t);
  return questions.map((q, idx) => {
    const text = q.question || "Question";
    const type = normalizeQuestionType(q.question_type);
    const options = extractOptions(q);
    const key = q.question_code || String(q.id ?? idx);
    return { key: String(key), text, type, options, answer: q.answer };
  });
});

watch(
  () => selectedTemplateId.value,
  () => {
    for (const k of Object.keys(answers)) delete answers[k];
    for (const q of selectedQuestions.value) {
      if (q.type === "checkbox") {
        answers[q.key] = coerceCheckboxAnswer((q as any).answer);
        continue;
      }
      if (q.type === "date") {
        answers[q.key] = coerceDateAnswer((q as any).answer);
        continue;
      }
      const a = (q as any).answer;
      answers[q.key] = a == null ? "" : a;
    }
  }
);

async function loadSurveyTemplates() {
  try {
    const response = await fetchSurveySavedTemplates();
    // eslint-disable-next-line no-console
    console.log("fetchSurveySavedTemplates response:", response);
    const maybeWrapped = (response as any)?.data;
    const list = Array.isArray(maybeWrapped) ? maybeWrapped : response;
    surveyTemplates.value = Array.isArray(list) ? list : [];
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error("fetchSurveySavedTemplates failed", error);
  }
}

async function handleSubmitSurvey() {
  const rawAppointmentId =
    (route.params as any)?.appointmentId ?? (route.params as any)?.id ?? "";
  const appointmentId = Number(rawAppointmentId);
  if (!Number.isInteger(appointmentId)) return;
  const payload = {
    survey_id: selectedTemplateId.value,
    survey_data: selectedQuestions.value.map((q) => ({
      question_code: q.key,
      question: q.text,
      question_type: q.type,
      options: q.options,
      answer: answers[q.key],
    })),
  };
  try {
    isSubmitting.value = true;
    const response = await submitAppointmentSurvey(appointmentId, payload);
    isSubmitting.value = false;
    const rawSubmittedId = response?.data?.id;
    const submittedId = Number(rawSubmittedId);

    await router.push({
      name: "provider.survey.success",
      params: { id: Number.isInteger(submittedId) ? submittedId : appointmentId },
    });
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error("submitAppointmentSurvey failed:", error);
  }
}

onMounted(async () => {
  await loadSurveyTemplates();
});
</script>

<style scoped>
.survey-form {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 12px;
}
.survey-action-section {
  position: fixed;
  bottom: 0;
  background-color: #fff;
  width: 100%;
  padding: 10px 14px;
  border-top: 1px solid #fff;
}
.survey-form__helper {
  margin-top: -4px;
}

.survey-form__helper-btn {
  text-transform: capitalize;
  letter-spacing: 0.06em;
  text-decoration: underline;
}

.questions__title {
  font-weight: 700;
  color: var(--color-title, #0f172a);
  margin-bottom: 8px;
}

.questions__list {
  margin: 0;
  color: var(--color-subtitle, #475569);
  display: grid;
  gap: 6px;
}

.question__text {
  margin-bottom: 8px;
  color: var(--color-title, #0f172a);
  font-weight: 500;
  font-size: 0.875rem;
}

.question__unsupported {
  font-size: 13px;
  color: #b45309;
}

.question__textarea :deep(textarea) {
  font-size: 0.875rem;
  padding: 10px 16px;
}

.empty-state {
  margin-top: 12px;
  padding: 18px 16px;
  border: 1px dashed #e5e7eb;
  border-radius: 14px;
  background: #fafafa;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 8px;
}

.empty-state__icon {
  width: 56px;
  height: 56px;
  border-radius: 16px;
  display: grid;
  place-items: center;
  background: rgba(var(--v-theme-primary), 0.08);
}

.empty-state__title {
  font-weight: 800;
  color: var(--color-title, #0f172a);
}

.empty-state__subtitle {
  max-width: 520px;
  font-size: 14px;
  color: var(--color-subtitle, #475569);
  line-height: 1.4;
}
</style>
